import userTypes from 'constants/userTypes';
import cancelationTypes from 'constants/cancelationTypes';

import { tenderParticipantStatusTypes } from 'utils/tenderOfferTypes';
import { calculateConsolidationPercent } from 'utils/consolidationPeriods';

const isPlanStartDateBeforeEndDate = (beneficiary, endDate) =>
  new Date(beneficiary.planStartDate) < new Date(endDate);

const isCancelationDateBeforeEndDate = (beneficiary, endDate) =>
  beneficiary.isCanceled &&
  new Date(beneficiary?.cancelationDate) < new Date(endDate);

const isOperationDateBeforeEndDate = (operationDate, endDate) =>
  new Date(operationDate) < new Date(endDate);

const isFullCancelled = (beneficiary) =>
  beneficiary?.isCanceled &&
  beneficiary?.cancelationOption === cancelationTypes.FULL;

const hasParticipantAcceptedUnits = (participant) =>
  participant?.status === tenderParticipantStatusTypes.ACCEPTED.value &&
  participant?.totalAccepted;

export function getBeneficiariesSummary({
  beneficiaries,
  plans,
  consolidationPeriods,
  tenderOffers,
  endDate,
  hasCif = false,
}) {
  if (!plans.length || !beneficiaries.length) return null;

  const summary = beneficiaries.reduce((acc, beneficiary) => {
    acc[beneficiary['_id']] = {
      assigned: 0,
      vested: 0,
      canceled: 0,
      acquired: 0,
    };

    if (hasCif) {
      acc[beneficiary['_id']].cif = beneficiary?.cif.toUpperCase() || '';
    }
    return acc;
  }, {});

  beneficiaries.forEach((beneficiary) => {
    let currentAssigned = 0;
    let lastDate = new Date();

    if (isPlanStartDateBeforeEndDate(beneficiary, endDate)) {
      if (summary[beneficiary['_id']]) {
        currentAssigned = beneficiary?.sharesCount?.future || 0;
        lastDate = new Date(beneficiary.planStartDate);
      }
    }

    if (isCancelationDateBeforeEndDate(beneficiary, endDate)) {
      if (summary[beneficiary['_id']]) {
        currentAssigned = beneficiary?.finalConsolidatedUnits || 0;
        lastDate = new Date(beneficiary?.cancelationDate);
        summary[beneficiary['_id']].canceled =
          beneficiary?.sharesCount?.future -
            beneficiary?.finalConsolidatedUnits || 0;
      }
    }

    const beneficiaryConsolidationPeriods =
      consolidationPeriods[beneficiary['_id']] || [];

    // Calculate vested units based on beneficiary status
    if (beneficiary.isCanceled) {
      // For canceled beneficiaries, use finalConsolidatedUnits if cancellation happened before endDate
      const cancelationDate = new Date(beneficiary.cancelationDate);
      if (cancelationDate <= new Date(endDate)) {
        summary[beneficiary['_id']].vested =
          beneficiary.finalConsolidatedUnits || 0;
      }
    } else {
      // For active beneficiaries, find the most recent consolidation period up to endDate
      const closestPeriod = beneficiaryConsolidationPeriods.reduce(
        (acc, period) => {
          if (new Date(period.date).getTime() <= new Date(endDate).getTime()) {
            if (
              !acc ||
              new Date(acc.date).getTime() < new Date(period.date).getTime()
            ) {
              return period;
            }
          }
          return acc;
        },
        null
      );

      if (closestPeriod) {
        summary[beneficiary['_id']].vested =
          closestPeriod.totalVestedUnits || 0;
      } else {
        // FALLBACK: If no consolidation periods found (e.g., vestingPeriod = 0),
        // calculate vested units based on consolidation percentage
        // This handles cases where beneficiaries have immediate vesting or zero vesting period
        const plan = plans.find((p) => p['_id'] === beneficiary.plan);
        if (plan) {
          const percent = calculateConsolidationPercent(
            endDate,
            beneficiary,
            plan
          );
          summary[beneficiary['_id']].vested = Math.floor(
            (percent * (beneficiary?.sharesCount?.future || 0)) / 100
          );
        }
      }
    }

    const beneficiaryTenders = beneficiary.soldUnits || [];

    const tenders = tenderOffers.filter((tender) =>
      beneficiaryTenders.includes(tender['_id'])
    );

    tenders.forEach((tender) => {
      const participants = tender.participants.filter(
        (participant) => participant.id === beneficiary['_id']
      );
      participants.forEach((participant) => {
        const { type, totalAccepted, operationDate } = participant;

        const isValidParticipant =
          hasParticipantAcceptedUnits(participant) &&
          isOperationDateBeforeEndDate(operationDate, endDate);

        if (type !== userTypes.BENEFICIARY || !isValidParticipant) return;

        if (summary[beneficiary['_id']]) {
          summary[beneficiary['_id']].acquired += totalAccepted || 0;
          if (new Date(operationDate) > lastDate) {
            currentAssigned -= totalAccepted || 0;
          }
        }
      });
    });

    summary[beneficiary['_id']].assigned = currentAssigned;
  });

  return summary;
}
